import React, {
    useEffect,
    useState,
    useContext,
    useCallback
} from 'react';
import { Redirect } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { Wizard } from 'e1p-portals-wizard-react';
import { RenewalService } from 'e1p-capability-renewal';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { ErrorBoundary } from '@xengage/gw-portals-error-react';
import { error as loggerError } from '@jutro/logger';
import { messages as platformMessages } from '@xengage/gw-platform-translations';
import { OnPageOperationsUtil } from 'e1p-portals-util-js';
import { E1PLoader } from 'e1p-capability-policyjob-react';
import { ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import ho3RenewalWizardConfig from './config/HO3/eh-wizard-config.json5';
import ho4RenewalWizardConfig from './config/HO4/eh-wizard-config.json5';
import ho6RenewalWizardConfig from './config/HO6/eh-wizard-config.json5';
import hf9RenewalWizardConfig from './config/HF9/eh-wizard-config.json5';
import ho3ViewWizardConfig from './config/viewOnly/HO3/eh-wizard-config.json5';
import ho4ViewWizardConfig from './config/viewOnly/HO4/eh-wizard-config.json5';
import ho6ViewWizardConfig from './config/viewOnly/HO6/eh-wizard-config.json5';
import hf9ViewWizardConfig from './config/viewOnly/HF9/eh-wizard-config.json5';
import wizardStepsToFieldsMappings from './config/eh-wizard-step-to-field-mapping.json5';
import messages from './EHRenewalWizard.messages';
import styles from './EHRenewalWizard.module.scss';

const policyTypeMapViewOnly = {
    HO3: {
        wizardConfig: ho3ViewWizardConfig
    },
    HO4: {
        wizardConfig: ho4ViewWizardConfig
    },
    HO6: {
        wizardConfig: ho6ViewWizardConfig
    },
    HF9: {
        wizardConfig: hf9ViewWizardConfig
    }
};
const policyTypeMap = {
    HO3: {
        wizardConfig: ho3RenewalWizardConfig
    },
    HO4: {
        wizardConfig: ho4RenewalWizardConfig
    },
    HO6: {
        wizardConfig: ho6RenewalWizardConfig
    },
    HF9: {
        wizardConfig: hf9RenewalWizardConfig
    }
};

function EHRenewalWizard(props) {
    const modalApi = useModal();
    const [wizardConfig, setWizardConfig] = useState(ho3RenewalWizardConfig);
    // const [viewOnly, setViewOnly] = useState(false);
    const { steps, title } = wizardConfig;
    const [viewOnly, setViewOnly] = useState(false);
    const [initialRenewal, setInitialRenewal] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasErrorOccurred, setHasErrorOccurred] = useState(false);
    const { onPageCancel, onPagePrevious } = OnPageOperationsUtil();
    const viewModelService = useContext(ViewModelServiceContext);
    const { location, history, authHeader } = props;
    const [
        isPageJumpEnabled,
        updateIsPageJumpEnabled
    ] = useState(false);


    useEffect(() => {
        if (location.state.viewOnly) {
            setViewOnly(true);
            setWizardConfig(policyTypeMapViewOnly[location.state.policyType].wizardConfig);
        } else {
            setWizardConfig(policyTypeMap[location.state.policyType].wizardConfig);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFinish = useCallback((finishParams) => {
        const {
            wizardData: renewalVM,
        } = finishParams;
        const jobID = _.get(renewalVM.value, 'jobID');

        return history.push(`/renewal/${jobID}/summary`);
    }, [history]);

    useEffect(() => {
        if (!location.state) {
            history.push('/');

            return;
        }

        const {
            policyNumber, jobNumber, clickedButtonValue
        } = location.state;
        // if (location.state.viewOnly) {
        //     setViewOnly(true);
        //     setWizardConfig(
        //         policyTypeMapViewOnly[location.state.policyType]
        //             ? policyTypeMapViewOnly[location.state.policyType].wizardConfig
        //             : ho3ViewWizardConfig
        //     );
        // }
        const errorModalBox = (errorMessage) => {
            modalApi.showConfirm(errorMessage).then(() => {
                setIsLoading(false);

                let path = `/contactAgent/${policyNumber}`;

                if (jobNumber) {
                    path = `/renewal/${jobNumber}/summary`;
                }

                history.push(path);
            });
        };

        if (clickedButtonValue === 'editRenewal') {
            RenewalService.startEditRenewal(
                jobNumber,
                authHeader
            ).then((response) => {
                const renewal = viewModelService.create(
                    response,
                    'pc',
                    'edge.capabilities.renewal.dto.RenewalDataDTO'
                );

                setInitialRenewal(renewal);
                updateIsPageJumpEnabled(true);
                setIsLoading(false);
            });
        } else if (jobNumber) {
            RenewalService.retrieveRenewal([jobNumber], authHeader)
                .then((responseData) => {
                    const renewal = viewModelService.create(
                        responseData,
                        'pc',
                        'edge.capabilities.renewal.dto.RenewalDataDTO'
                    );

                    // need to map the producer code over
                    // producer code is assigned from the submission and cannot be changed
                    _.set(renewal, 'baseData.producerCode_Ext.value',
                        responseData.baseData.producerCode);
                    setInitialRenewal(renewal);
                    updateIsPageJumpEnabled(true);
                    setIsLoading(false);
                }).catch(() => {
                    errorModalBox({
                        title: messages.unableToLoadDraftPolicy,
                        message: messages.somethingWentWrong,
                        status: 'warning',
                        icon: 'mi-error-outline'
                    });
                });
        } else if (policyNumber) {
            RenewalService.createRenewalTransaction(
                policyNumber,
                authHeader
            ).then((response) => {
                const renewal = viewModelService.create(
                    response,
                    'pc',
                    'edge.capabilities.renewal.dto.RenewalDataDTO'
                );

                setInitialRenewal(renewal);
                setIsLoading(false);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOnCancel = useCallback((cancelParams) => {
        const {
            wizardData: renewalVM,
            wizardSnapshot,
            currentStepIndex,
            param = {}
        } = cancelParams;
        const { search, pathname: nextPath = '' } = param;
        const skipModal = ['knockoutpage', 'logout', 'summary'];
        let lastPathName = '';

        if (nextPath) {
            lastPathName = nextPath.replace(/\/?.*\//g, '');
        }

        const isStatusBound = _.get(wizardSnapshot, 'value.status') === 'Bound';

        if ((!_.isEmpty(nextPath) && nextPath.startsWith('/contactAgent'))
            || hasErrorOccurred
            || (isStatusBound && currentStepIndex > 0)
            || skipModal.includes(lastPathName)
            || viewOnly
        ) {
            return true;
        }

        return Promise.resolve(onPageCancel(renewalVM.value, wizardSnapshot.value)
            .then((popupResult) => {
                if (!popupResult || popupResult === 'cancel') {
                    return _.noop();
                }

                setIsLoading(true);

                const jobID = _.get(renewalVM.value, 'jobID');
                const policynumber = _.get(renewalVM.value, 'policyNumber');
                let redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/renewal/${jobID}/summary`;

                if (jobID === undefined) {
                    redirectLobPath = (!_.isEmpty(nextPath) && nextPath) || `/policies/${policynumber}/summary`;
                }

                if (nextPath?.includes('/quote-')) {
                    return history.push({
                        pathname: nextPath,
                        search
                    });
                }

                return history.push(redirectLobPath);
            }).catch(_.noop()));
    }, [hasErrorOccurred, history, onPageCancel, viewOnly]);

    const handlePrevious = useCallback((previousProps) => {
        const {
            wizardData, wizardSnapshot, updateWizardData, cloneData
        } = previousProps;

        return Promise.resolve(
            onPagePrevious(
                wizardData, wizardSnapshot,
                updateWizardData, cloneData, viewOnly
            )
        );
    }, [onPagePrevious, viewOnly]);

    const handleError = useCallback((error) => {
        const redirectPath = '/contact-for-assistance';
        const pathname = _.get(location, 'pathname');
        const state = {
            pathname: redirectPath,
            state: error,
            policyNumber: _.get(location, 'state.policyNumber')
        };

        loggerError(`MSA-EHWizard-Error: ${error}
            Pathname: ${pathname}`);
        setHasErrorOccurred(true);

        return <Redirect to={state} />;
    }, [location]);

    if (isLoading) {
        return (
            <E1PLoader loaded={!isLoading} />
        );
    }

    if (!initialRenewal) {
        return null;
    }

    const handleDiscardChanges = async (discardChangeParams) => {
        const {
            wizardData: renewalVM,
            updateWizardData
        } = discardChangeParams;

        return modalApi.showConfirm({
            status: 'warning',
            icon: 'mi-error-outline',
            title: platformMessages.discardChanges,
            message: platformMessages.discardChangeMessage,
            confirmButtonText: platformMessages.discardChangeConfirmationYes,
            cancelButtonText: platformMessages.discardChangeCancel
        }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }

            setIsLoading(true);

            const jobID = _.get(renewalVM.value, 'jobID');
            const redirectedPath = `/renewal/${jobID}/summary`;

            RenewalService.discardRenewalChanges(jobID, authHeader).then((responseData) => {
                if (responseData.baseData?.exceptions_Ext) {
                    _.set(renewalVM, 'value.baseData.exceptions_Ext', responseData.baseData?.exceptions_Ext);
                    updateWizardData(renewalVM);

                    return false;
                }

                _.set(renewalVM, 'value', responseData);
                updateWizardData(renewalVM);

                return history.push(redirectedPath);
            })
                .catch(() => {
                    _.noop();
                }).finally(() => {
                    setIsLoading(false);
                });

            return true;
        }, _.noop);
    };

    const handleApplyChanges = async (applyChangeParams) => {
        const {
            wizardData: renewalVM,
            updateWizardData
        } = applyChangeParams;

        return modalApi.showConfirm({
            status: 'warning',
            icon: 'mi-error-outline',
            title: platformMessages.applyChanges,
            message: platformMessages.applyChangeMessage,
            confirmButtonText: platformMessages.applyChangeConfirmationYes,
            cancelButtonText: platformMessages.applyChangeCancel
        }).then((results) => {
            if (results === 'cancel') {
                return _.noop();
            }

            setIsLoading(true);

            const jobID = _.get(renewalVM.value, 'jobID');
            const redirectedPath = `/renewal/${jobID}/summary`;

            RenewalService.applyRenewalChanges([jobID], authHeader).then((responseData) => {
                _.set(renewalVM, 'value', responseData);
                updateWizardData(renewalVM);

                return history.push(redirectedPath);
            })
                .catch(() => {
                    _.noop();
                }).finally(() => {
                    setIsLoading(false);
                });

            return true;
        }, _.noop);
    };


    return (
        <div className={styles.hoRenewalWizardContainer}>
            <ErrorBoundary onError={handleError}>
                {/* Hidden field needed for GA on each wizard page */}
                <p id="sourceOfEntry" style={{ display: 'none' }}>
                    {_.get(
                        initialRenewal,
                        'baseData.quoteSource_Ext.sourceType.value.code',
                        'directentry'
                    )}
                </p>
                <Wizard
                    initialSteps={steps}
                    skipCompletedSteps={false}
                    wizardStepToFieldMapping={wizardStepsToFieldsMappings}
                    wizardTitle={title}
                    initialData={initialRenewal}
                    onCancel={handleOnCancel}
                    onFinish={handleFinish}
                    onPrevious={handlePrevious}
                    viewOnly={viewOnly}
                    isRenewal
                    onCustom1={handleApplyChanges}
                    onCustom2={handleDiscardChanges}
                    onPreviousModalProps={{
                        title: platformMessages.wantToJump,
                        message: platformMessages.wantToJumpMessage,
                        messageProps: {
                            ok: platformMessages.yes,
                            close: platformMessages.no
                        }
                    }}
                    isPageJumpEnabled={isPageJumpEnabled}
                />
            </ErrorBoundary>
        </div>
    );
}

EHRenewalWizard.propTypes = {
    location: PropTypes.shape({
        state: PropTypes.shape({
            address: PropTypes.shape({}),
            policyNumber: PropTypes.string,
            policyType: PropTypes.string,
            viewOnly: PropTypes.bool,
            latestPeriod: PropTypes.shape({
                effectiveDate: PropTypes.string,
                cancellationDate: PropTypes.string
            })
        }),
    }).isRequired,
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired,
    authHeader: PropTypes.shape({
        Authorization: PropTypes.string
    }),
};

export default withAuthenticationContext(EHRenewalWizard);
